import { useEffect, useMemo, useState } from "react";
import { LockIcon, LockOpenIcon, XIcon } from "lucide-react";

import { FilterType } from "@/components/@types";
import { FilterChipNameSection } from "@/components/organisms/UtilityBar/FilterChip/FilterChipNameSection";
import { FilterChipOperatorSection } from "@/components/organisms/UtilityBar/FilterChip/FilterChipOperatorSection";
import { FilterChipValueSection } from "@/components/organisms/UtilityBar/FilterChip/FilterChipValueSection";
import { toTestId } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.tools";
import {
  useFilterChipMutations,
  useFilterChipState,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChipStateContext";
import {
  flattenFilterOperators,
  getSingleFilterOperator,
  isFilterOperatorUnset,
  normalizeFilterOperator,
} from "@/components/organisms/UtilityBar/FilterChip/FilterChip.utils";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import { ButtonGroup } from "@/components/ui/button-group";

export function FilterChip({
  filterKey,
  filter,
  filterIndex,
  showLockToggle = false,
  compact = false,
}: {
  filterKey: string;
  filter: FilterType;
  filterIndex: number;
  /** When true, renders a lock toggle before the close button. */
  showLockToggle?: boolean;
  /** Icon-only name, operator symbols, and value — for narrow layouts. */
  compact?: boolean;
}) {
  const { filterDefinitions, isControlled } = useFilterChipState();
  const { removeFilterAtIndex, updateFilterAtIndex } = useFilterChipMutations();
  const filterDefinition = filterDefinitions?.[filterKey];

  const operators = useMemo(
    () => flattenFilterOperators(filterDefinition?.operators),
    [filterDefinition],
  );
  const normalizedOperator = normalizeFilterOperator(filter.operator);
  const operator = operators.find((op) => op.key === normalizedOperator);
  let operatorName = operator?.name;
  // For "isNull" onlyOperator, reflect the value in the display name
  if (
    filter.operator === "isNull" &&
    operator?.onlyOperator &&
    filter.value === false
  ) {
    operatorName = "Is Not Null";
  }
  const operatorOnly = operator?.onlyOperator === true;

  const ReactIcon = filterDefinition?.ReactIcon;
  const [isNameOpen, setIsNameOpen] = useState(false);
  const [isOperatorOpen, setIsOperatorOpen] = useState(false);
  const [isValueOpen, setIsValueOpen] = useState(false);

  const isLocked = filter.isLocked === true;

  // When a field is selected but the operator is unset, preselect a lone
  // operator or open the operator picker for multi-operator fields.
  useEffect(() => {
    if (isLocked || !filterDefinition) {
      return;
    }

    if (!isFilterOperatorUnset(filter, operators)) {
      return;
    }

    const singleOperator = getSingleFilterOperator(filterDefinition);
    if (singleOperator?.key) {
      updateFilterAtIndex(
        filterIndex,
        (current) =>
          ({
            ...current,
            operator: singleOperator.key as FilterType["operator"],
            value: singleOperator.onlyOperator ? true : null,
          }) as FilterType,
      );
      if (!singleOperator.onlyOperator) {
        setIsValueOpen(true);
      }
      return;
    }

    setIsOperatorOpen(true);
  }, [
    filter,
    filterDefinition,
    filterIndex,
    isLocked,
    operators,
    updateFilterAtIndex,
  ]);

  // Opened from outside the bar - a column header's "Filter" - at what there
  // is to fill in: the value, or the operator where it takes none. Only the
  // open list's chips answer it, not a bar's over a list of its own.
  const openFilterKey = State((state) => state.openFilterKey);
  useEffect(() => {
    if (openFilterKey !== filterKey || isControlled) {
      return;
    }
    State.setState({ openFilterKey: null });
    if (isLocked) {
      return;
    }
    if (operatorOnly || normalizedOperator == null) {
      setIsOperatorOpen(true);
    } else {
      setIsValueOpen(true);
    }
  }, [openFilterKey]);

  if (!filterDefinition) return null;

  const testId = toTestId(filterDefinition.name);
  // Its name, its operator and its value, a button each - or, locked, words
  // in the group - then its lock and its x: one of shadcn's button groups, at
  // a bar's size (``h-8``, ``sm``'s, for a locked chip, which is words alone).
  return (
    <ButtonGroup data-testid={`filter-chip-${testId}-set`} className="h-8">
      <FilterChipNameSection
        isLocked={isLocked}
        ReactIcon={ReactIcon}
        filterDefinition={filterDefinition}
        isNameOpen={isNameOpen}
        setIsNameOpen={setIsNameOpen}
        filterIndex={filterIndex}
        compact={compact}
      />
      <FilterChipOperatorSection
        filterDefinition={filterDefinition}
        filter={filter}
        isLocked={isLocked}
        operatorName={operatorName}
        isOperatorOpen={isOperatorOpen}
        setIsOperatorOpen={setIsOperatorOpen}
        operators={operators}
        filterIndex={filterIndex}
        compact={compact}
      />
      {normalizedOperator != null && !operatorOnly && (
        <FilterChipValueSection
          filter={filter}
          filterDefinition={filterDefinition}
          isLocked={isLocked}
          isValueOpen={isValueOpen}
          setIsValueOpen={setIsValueOpen}
          filterIndex={filterIndex}
        />
      )}
      {/* A dashboard widget's filter is locked and let go here. */}
      {showLockToggle && (
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={isLocked ? "Unlock filter" : "Lock filter"}
          aria-pressed={isLocked}
          data-testid={`filter-chip-${testId}-lock`}
          onClick={(event) => {
            event.stopPropagation();
            updateFilterAtIndex(filterIndex, (current) => ({
              ...current,
              isLocked: !current.isLocked,
            }));
          }}
        >
          {isLocked ? <LockIcon size="1em" /> : <LockOpenIcon size="1em" />}
        </Button>
      )}
      {!isLocked && (
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Remove filter"
          data-testid={`filter-chip-${testId}-close`}
          onClick={(event) => {
            event.stopPropagation();
            removeFilterAtIndex(filterIndex);
            requestAnimationFrame(() => {
              const addFilterControl = document.querySelector(
                '[data-testid="filter-chip-add-filter"], [data-testid="filter-chip-add-more"]',
              ) as HTMLElement;
              addFilterControl?.focus();
            });
          }}
        >
          <XIcon size="1em" />
        </Button>
      )}
    </ButtonGroup>
  );
}
